import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { ClerkProvider } from '@clerk/clerk-react'
import { HelmetProvider } from 'react-helmet-async'
import { Toaster } from 'react-hot-toast'
import App from './App.tsx'
import CookieConsent from './components/CookieConsent'
import './index.css'
import './design-system/tokens.css'
import { APP_VERSION, BUILD_DATE } from './version'
import { isChunkLoadError, triggerChunkErrorReload } from './components/ChunkErrorBoundary'
import { captureAttribution } from './utils/attribution'

// Log build version for debugging
console.log(`🚀 Super Schema v${APP_VERSION} - Built: ${BUILD_DATE}`)

// Capture first-touch UTM attribution before anything else can mutate the URL
captureAttribution(window.location.search, window.location.pathname)

// Update build timestamp in HTML
const timestampMeta = document.getElementById('build-timestamp')
if (timestampMeta) {
  timestampMeta.setAttribute('content', BUILD_DATE)
}

// Catch chunk load errors globally (for errors that escape the boundary)
// Uses shared utilities to prevent double reloads and infinite loops
window.addEventListener('error', (event) => {
  // Create a synthetic error object to check with shared utility
  const error = {
    name: '',
    message: event.message || ''
  } as Error

  if (isChunkLoadError(error)) {
    console.log('Chunk load error detected globally')
    const shouldReload = triggerChunkErrorReload()
    if (shouldReload) {
      window.location.reload()
    }
  }
})

const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY

if (!clerkPubKey) {
  throw new Error('Missing Clerk Publishable Key')
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <HelmetProvider>
      <ClerkProvider publishableKey={clerkPubKey}>
        <BrowserRouter>
          <App />
          {/*
            The cookie question sits beside the application rather than
            inside it: App returns a loading screen of its own while Clerk
            initialises, and a visitor waiting on that screen is still a
            visitor we have not asked.
          */}
          <CookieConsent />
          {/*
            Every toast in the product, drawn from the tokens rather than the
            shadcn variables it used to borrow.

            The scope goes on each TOAST, never on the container. The
            container react-hot-toast renders is `position: fixed` covering
            the whole viewport, and `.ds` paints an opaque background, so
            putting the scope there lays a full-screen sheet of paper over
            the entire application at z-index 9999. It is invisible to every
            test in this repo, because jsdom renders no toast until one is
            fired and computes no paint in any case. It white-screened
            staging.
          */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              className: 'ds',
              style: {
                background: 'var(--ds-paper)',
                color: 'var(--ds-ink)',
                border: '1px solid var(--ds-hairline)',
                borderRadius: 'var(--ds-radius-lg)',
                boxShadow: 'var(--ds-shadow-float)',
                fontFamily: 'var(--ds-font)',
                fontSize: '15px',
                lineHeight: '21px',
                padding: '12px 14px',
              },
              success: { iconTheme: { primary: 'var(--ds-good)', secondary: 'var(--ds-on-good)' } },
              error: { iconTheme: { primary: 'var(--ds-danger-text)', secondary: 'var(--ds-paper)' } },
            }}
          />
        </BrowserRouter>
      </ClerkProvider>
    </HelmetProvider>
  </React.StrictMode>,
)